import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Trophy, TrendingUp, TrendingDown, Sparkles, RefreshCw, ChevronDown, ChevronUp, Info, BarChart3, Grid3X3, Users, Lightbulb, ArrowRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, BarChart, Bar, Cell } from 'recharts';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { getLightSectionTheme } from '../../lib/sectionThemes';
import { useOrgAnonymizer } from '../../hooks/useOrgAnonymizer';
import { OrgFilterBar } from '../../components/benchmarking/OrgFilterBar';
import { FilterEmptyState } from '../../components/benchmarking/FilterEmptyState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const orgColors = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#0ea5e9', '#ec4899'];

// Helper: convert 0–1 decimal to percentage for display
const pct = (v) => v != null ? (v * 100).toFixed(1) : '0.0';

const getPEColor = (score) => {
  if (score >= 0.80) return '#10b981';
  if (score >= 0.60) return '#f59e0b';
  if (score >= 0.40) return '#f97316';
  return '#ef4444';
};

const getPEBg = (score) => {
  if (score >= 0.80) return 'bg-emerald-100 text-emerald-800';
  if (score >= 0.60) return 'bg-amber-100 text-amber-800';
  if (score >= 0.40) return 'bg-orange-100 text-orange-800';
  return 'bg-rose-100 text-rose-800';
};

// Missing Middle concept grouping — frames the raw PE components as the
// upstream workforce conditions that influence value transfer. Labels +
// insights only; no calculation or data-shape changes.
const CONCEPT_MAP = {
  capability: {
    legacy: 'Capability',
    label: 'Readiness',
    hint: 'Readiness — whether the workforce has the skills and knowledge in place to perform.',
  },
  motivation: {
    legacy: 'Motivation',
    label: 'Confidence & Intent',
    hint: 'Confidence & Intent — the willingness and drive to apply that capability on the job.',
  },
  retention: {
    legacy: 'Retention',
    label: 'Sustained Performance Conditions',
    hint: 'Sustained Performance Conditions — the conditions that keep capable, motivated people performing over time.',
  },
};

const TONE_BG = {
  emerald: 'bg-emerald-100 text-emerald-800',
  amber: 'bg-amber-100 text-amber-800',
  orange: 'bg-orange-100 text-orange-800',
  rose: 'bg-rose-100 text-rose-800',
};

// Qualitative state of a single workforce condition (0–1 scale).
const conditionState = (v) => {
  if (v >= 0.80) return { label: 'Strong', tone: 'emerald' };
  if (v >= 0.60) return { label: 'Healthy', tone: 'amber' };
  if (v >= 0.40) return { label: 'Emerging', tone: 'orange' };
  return { label: 'Constrained', tone: 'rose' };
};

// Workforce condition → Solution Finder KPI (mirrors the Industry/Org
// Benchmarks gap-bridge convention so arriving with ?kpi=<id> pre-selects
// the KPI, advances to Select Intervention, and shows the gap banner).
const PE_CONDITION_TO_SF_KPI = {
  capability: 'productivity',
  motivation: 'engagement',
  retention: 'mobility',
};

const HeatCell = ({ value }) => {
  if (value == null) return <td className="px-3 py-2 text-center text-slate-300">—</td>;
  const intensity = Math.min(100, Math.max(0, value * 100));
  const r = intensity < 50 ? 239 : Math.round(239 - (intensity - 50) * 4.58);
  const g = intensity < 50 ? Math.round(68 + intensity * 3.42) : 181;
  const b = intensity < 50 ? 68 : Math.round(68 + (intensity - 50) * 2.26);
  return (
    <td className="px-3 py-2 text-center">
      <span
        className="inline-block px-2 py-0.5 rounded text-xs font-bold"
        style={{ backgroundColor: `rgba(${r},${g},${b},0.2)`, color: `rgb(${r},${g},${b})` }}
      >
        {pct(value)}%
      </span>
    </td>
  );
};

// Markdown renderer (reused from diagnostic)
const MarkdownRenderer = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  for (const line of lines) {
    i++;
    if (line.startsWith('## ')) {
      elements.push(<h3 key={`h3-${i}`} className="text-base font-semibold text-slate-800 mt-4 mb-2">{line.replace('## ', '')}</h3>);
    } else if (line.startsWith('### ')) {
      elements.push(<h4 key={`h4-${i}`} className="text-sm font-semibold text-slate-700 mt-3 mb-1">{line.replace('### ', '')}</h4>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <li key={`li-${i}`} className="text-sm text-slate-600 ml-4 mb-1 list-disc">
          {line.replace(/^[-*]\s/, '').split('**').map((part, j) =>
            j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
          )}
        </li>
      );
    } else if (line.match(/^\d+\.\s/)) {
      elements.push(
        <li key={`ol-${i}`} className="text-sm text-slate-600 ml-4 mb-1 list-decimal">
          {line.replace(/^\d+\.\s/, '').split('**').map((part, j) =>
            j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
          )}
        </li>
      );
    } else if (line.trim()) {
      elements.push(
        <p key={`p-${i}`} className="text-sm text-slate-600 mb-2">
          {line.split('**').map((part, j) =>
            j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
          )}
        </p>
      );
    }
  }
  return <div>{elements}</div>;
};

export default function PEBenchmarkingPage() {
  const { viewAsOrg, isSuperAdmin, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showCohorts, setShowCohorts] = useState(false);
  const [accessRestricted, setAccessRestricted] = useState(null);
  // Mirror Org Benchmarks: org → industry/region/size lookup for
  // filters, chips, and anonymization. Empty maps for non-super-admin
  // users is fine — filters just won't light up.
  const [orgIndustryMap, setOrgIndustryMap] = useState({});
  const [orgRegionMap, setOrgRegionMap] = useState({});
  const [orgSizeMap, setOrgSizeMap] = useState({});
  const [industries, setIndustries] = useState([]);
  const [industryFilter, setIndustryFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');

  const [trendMetric, setTrendMetric] = useState('pe');
  // Lookups + anonymization — fetched once, used everywhere.
  useEffect(() => {
    (async () => {
      try {
        const [orgsRes, indRes] = await Promise.all([
          api.get('/rbac/organizations'),
          api.get('/benchmarking/industries'),
        ]);
        const im = {}; const rm = {}; const sm = {};
        (orgsRes.data?.organizations || []).forEach(o => {
          if (o.industry_type) im[o.id] = o.industry_type;
          if (o.region) rm[o.id] = o.region;
          if (o.company_size) sm[o.id] = o.company_size;
        });
        setOrgIndustryMap(im);
        setOrgRegionMap(rm);
        setOrgSizeMap(sm);
        setIndustries(indRes.data?.industries || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const headers = useCallback(() => {
    const h = { 'Authorization': `Bearer ${secureStorage.getItem('session_token')}` };
    if (viewAsOrg?.id) h['X-View-As-Org'] = viewAsOrg.id;
    return h;
  }, [viewAsOrg]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setAccessRestricted(null);
      try {
        const res = await authedFetch(`${API_URL}/api/people-engine/benchmarking`, { headers: headers() });
        if (res.ok) {
          setData(await res.json());
        } else if (res.status === 403) {
          // RBAC: cross-org benchmarking is super-admin only. Surface a
          // tailored "premium feature" message rather than the generic
          // "no data" empty state (which used to imply a data issue).
          let msg = 'Cross-org benchmarking is restricted to platform administrators.';
          try {
            const err = await res.json();
            if (err?.detail) msg = err.detail;
          } catch { /* ignore */ }
          setAccessRestricted(msg);
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Failed to load benchmarking data');
        }
      } catch (e) {
        toast.error('Failed to load benchmarking data');
      }
      setLoading(false);
    };
    fetchData();
  }, [headers]);

  const generateInsights = async () => {
    setInsightsLoading(true);
    setShowInsights(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/benchmarking/insights`, { headers: headers() });
      if (res.ok) setInsights(await res.json());
      else toast.error('Failed to generate insights');
    } catch { toast.error('Failed to generate insights'); }
    setInsightsLoading(false);
  };

  // Centralized RBAC anonymization + filter predicate + chip renderers.
  // Must be called before any early returns so it stays a stable hook
  // call across renders. Safe to pass empty data: the hook just emits
  // empty lookups.
  const scopedOrgId = (viewAsOrg && viewAsOrg.id) || user?.organization_id || null;
  const {
    shouldAnonymize,
    orgLabelById,
    industryForOrg,
    regionForOrg,
    sizeForOrg,
    matchesFilters,
    availableRegions,
    availableSizes,
    OrgLabel,
    anonymizeNarrative,
    anonymizeCohortName,
  } = useOrgAnonymizer({
    isSuperAdmin,
    viewAsOrg,
    scopedOrgId,
    orgs: data?.org_scores || [],
    orgIndustryMap,
    orgRegionMap,
    orgSizeMap,
    industries,
    filters: { industry: industryFilter, region: regionFilter, size: sizeFilter },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="pe-benchmarking-page">
        <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (accessRestricted) {
    return (
      <div className="space-y-6" data-testid="pe-benchmarking-page">
        <h1 className="text-2xl font-bold text-slate-900">PE Benchmarking</h1>
        <Card data-testid="pe-benchmarking-restricted">
          <CardContent className="p-12 text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-indigo-600" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-1">
                Premium feature — cross-org benchmarking
              </h2>
              <p className="text-sm text-slate-600 max-w-xl mx-auto">
                {accessRestricted} Compare your PE Score, Capability,
                Motivation, Retention, and LEI metrics against an
                anonymised cohort of peer organisations to find
                exactly where you have room to grow.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Talk to your account team to enable peer benchmarking on
              your tenant.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data || !data.org_scores?.length) {
    return (
      <div className="space-y-6" data-testid="pe-benchmarking-page">
        <h1 className="text-2xl font-bold text-slate-900">PE Benchmarking</h1>
        <Card><CardContent className="p-12 text-center text-slate-500">No PE data available for benchmarking.</CardContent></Card>
      </div>
    );
  }

  const { org_scores: rawOrgScores, trends, heatmap, cohort_rankings, latest_period, periods } = data;

  // Filtered views — org_scores drives heatmap + trends so filters
  // propagate uniformly. Anonymization + chip rendering comes from the
  // useOrgAnonymizer hook above.
  const org_scores = rawOrgScores.filter((os) => matchesFilters(os.organization_id));
  const filteredIds = new Set(org_scores.map((o) => o.organization_id));
  const heatmapFiltered = (heatmap || []).filter((h) => filteredIds.has(h.org_id));

  // Build multi-org trend chart data — respects filter + anonymization.
  // The selected metric drives which field from each trend point lands
  // on the chart, so the y-axis stays comparable across orgs.
  const trendChartData = (periods || []).map(period => {
    const row = { period };
    for (const os of org_scores) {
      const orgTrend = trends[os.organization_id] || [];
      const match = orgTrend.find(t => t.period === period);
      row[orgLabelById[os.organization_id] || os.organization_name] =
        match ? match[trendMetric] : null;
    }
    return row;
  });

  const topOrg = org_scores[0];
  const bottomOrg = org_scores[org_scores.length - 1];
  const portfolioAvg = org_scores.length
    ? org_scores.reduce((sum, o) => sum + o.pe, 0) / org_scores.length
    : 0;

  // --- Missing Middle derived insights (labels/insights only, no math) ---
  const avgOf = (arr, key) => (arr.length ? arr.reduce((s, o) => s + (o[key] || 0), 0) / arr.length : 0);

  // Focal org for the readiness card: your org if it's in the cohort, else
  // the leading navigator.
  const focalOrg = org_scores.find((o) => o.organization_id === scopedOrgId) || topOrg;

  // Top-quartile (min 1) of the cohort by PE — "top performers".
  const topCount = Math.max(1, Math.round(org_scores.length * 0.25));
  const topPerformers = [...org_scores].sort((a, b) => b.pe - a.pe).slice(0, topCount);
  const peerDeltaPP = (key) => (avgOf(topPerformers, key) - avgOf(org_scores, key)) * 100;
  const conditionDeltas = ['capability', 'motivation', 'retention', 'lei'].map((k) => ({
    key: k,
    concept: CONCEPT_MAP[k]?.label || k.toUpperCase(),
    legacy: CONCEPT_MAP[k]?.legacy || k,
    delta: peerDeltaPP(k),
  }));

  // Cohort-wide strongest / weakest workforce condition (Navigation Signal).
  const condAverages = ['capability', 'motivation', 'retention'].map((k) => ({ key: k, avg: avgOf(org_scores, k) }));
  const weakestCond = condAverages.reduce((a, b) => (b.avg < a.avg ? b : a));
  const strongestCond = condAverages.reduce((a, b) => (b.avg > a.avg ? b : a));
  const opportunityGapPP = topOrg && bottomOrg ? (topOrg.pe - bottomOrg.pe) * 100 : 0;
  const leadingNavigatorName = topOrg ? (orgLabelById[topOrg.organization_id] || topOrg.organization_name) : '';

  // Value Transfer Readiness — combines the three conditions for the focal org.
  const vtrConditions = ['capability', 'motivation', 'retention'].map((k) => ({
    key: k,
    concept: CONCEPT_MAP[k].label,
    legacy: CONCEPT_MAP[k].legacy,
    value: focalOrg ? focalOrg[k] : null,
    state: conditionState(focalOrg ? focalOrg[k] : 0),
  }));
  const vtrComposite = focalOrg
    ? (focalOrg.capability + focalOrg.motivation + focalOrg.retention) / 3
    : 0;
  const vtrVerdict = conditionState(vtrComposite);
  // The focal org's lowest condition = its binding constraint (the per-org
  // action entry point into Solution Finder).
  const vtrLowest = vtrConditions.reduce((a, b) => ((b.value ?? 1) < (a.value ?? 1) ? b : a));
  const focalOrgName = focalOrg ? (orgLabelById[focalOrg.organization_id] || focalOrg.organization_name) : '';
  const vtrIsYou = focalOrg && focalOrg.organization_id === scopedOrgId;

  // Hero stats — reframed as a comparison of navigators (Leading Navigator /
  // Peer Average / Largest Opportunity) rather than isolated HR scores.
  const peHeroStats = [
    topOrg && {
      label: 'Leading Navigator',
      value: orgLabelById[topOrg.organization_id] || topOrg.organization_name,
      hint: `PE: ${pct(topOrg.pe)}%`,
      testId: 'pe-bench-hero-top',
    },
    {
      label: 'Peer Average',
      value: `${(portfolioAvg * 100).toFixed(1)}%`,
      hint: `${org_scores.length} organization${org_scores.length === 1 ? '' : 's'}`,
      testId: 'pe-bench-hero-avg',
    },
    bottomOrg && bottomOrg !== topOrg && {
      label: 'Largest Opportunity',
      value: orgLabelById[bottomOrg.organization_id] || bottomOrg.organization_name,
      hint: `PE: ${pct(bottomOrg.pe)}%`,
      testId: 'pe-bench-hero-bottom',
    },
  ].filter(Boolean);

  const __pageTheme = getLightSectionTheme('PE');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="PE Benchmarking"
        description="Compare the workforce conditions that influence value transfer and performance across peer organizations."
        theme={__pageTheme}
        icon={Users}
        stats={peHeroStats}
        actions={(
          <Link
            to="/benchmarks-reference"
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md transition-colors"
            data-testid="pe-benchmarks-references-link"
          >
            <BookOpen className="h-3.5 w-3.5" />
            References
          </Link>
        )}
        testId="pe-benchmarking-hero"
      />
    )}>
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6" data-testid="pe-benchmarking-page">

      {/* Filter row — industry / region / company size */}
      <OrgFilterBar
        industries={industries}
        availableRegions={availableRegions}
        availableSizes={availableSizes}
        industryValue={industryFilter}
        onIndustryChange={setIndustryFilter}
        regionValue={regionFilter}
        onRegionChange={setRegionFilter}
        sizeValue={sizeFilter}
        onSizeChange={setSizeFilter}
        testIdPrefix="pe-bench"
      />

      {org_scores.length === 0 && (
        <FilterEmptyState
          onClear={() => { setIndustryFilter('all'); setRegionFilter('all'); setSizeFilter('all'); }}
          testId="pe-bench-filter-empty"
        />
      )}

      {org_scores.length > 0 && (<>
      {/* 1. Org-vs-Org Comparison Table */}
      <Card data-testid="org-comparison-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4 text-indigo-500" />
            Organization Ranking
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Condition Interpretation — teaches the Missing Middle framing
              without renaming the table's metric columns. */}
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[11px] text-slate-500"
            data-testid="pe-condition-interpretation"
          >
            <span className="font-semibold uppercase tracking-wider text-slate-400">Condition Interpretation</span>
            <span><strong className="text-slate-600">Capability</strong> = Readiness</span>
            <span className="text-slate-300">·</span>
            <span><strong className="text-slate-600">Motivation</strong> = Confidence &amp; Intent</span>
            <span className="text-slate-300">·</span>
            <span><strong className="text-slate-600">Retention</strong> = Sustained Performance Conditions</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-2.5 px-3 text-slate-600 font-semibold">#</th>
                  <th className="text-left py-2.5 px-3 text-slate-600 font-semibold">Organization</th>
                  <th className="text-center py-2.5 px-3 text-slate-600 font-semibold">PE Score</th>
                  <th className="text-center py-2.5 px-3 text-slate-600 font-semibold">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-slate-300"
                          data-testid="pe-ppe-tooltip"
                        >
                          PPE <Info className="h-3 w-3 text-slate-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">Measures the effectiveness with which workforce capability converts into sustained performance.</p>
                      </TooltipContent>
                    </Tooltip>
                  </th>
                  <th className="text-center py-2.5 px-3 text-slate-600 font-semibold">Capability</th>
                  <th className="text-center py-2.5 px-3 text-slate-600 font-semibold">Motivation</th>
                  <th className="text-center py-2.5 px-3 text-slate-600 font-semibold">Retention</th>
                  <th className="text-center py-2.5 px-3 text-slate-600 font-semibold">LEI</th>
                </tr>
              </thead>
              <tbody>
                {org_scores.map((os, idx) => {
                  const isYou = os.organization_id === scopedOrgId;
                  return (
                    <tr
                      key={os.organization_id}
                      className={`border-b border-slate-50 ${idx === 0 ? 'bg-emerald-50/30' : ''} ${isYou ? 'ring-2 ring-blue-200' : ''}`}
                      data-testid={`pe-rank-row-${os.organization_id}`}
                    >
                      <td className="py-2.5 px-3">
                        {idx === 0 ? <Trophy className="h-4 w-4 text-amber-500" /> : <span className="text-slate-400 font-medium">{idx + 1}</span>}
                      </td>
                      <td className="py-2.5 px-3">
                        <OrgLabel orgId={os.organization_id} fallback={os.organization_name} />
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${getPEBg(os.pe)}`}>
                          {pct(os.pe)}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center text-slate-600 font-medium">{pct(os.ppe)}%</td>
                      <td className="py-2.5 px-3 text-center text-slate-600">{pct(os.capability)}%</td>
                      <td className="py-2.5 px-3 text-center text-slate-600">{pct(os.motivation)}%</td>
                      <td className="py-2.5 px-3 text-center text-slate-600">{pct(os.retention)}%</td>
                      <td className="py-2.5 px-3 text-center text-slate-600">{pct(os.lei)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Signal — deterministic read above the trend chart */}
      <Card className="border-l-4 border-l-indigo-400 bg-indigo-50/40" data-testid="pe-navigation-signal-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-indigo-500" />
            Navigation Signal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="pe-navigation-signal-text">
            <strong className="text-slate-900">{leadingNavigatorName}</strong> is the leading navigator at{' '}
            <strong>{pct(topOrg.pe)}%</strong>
            {opportunityGapPP > 0 && (
              <>, <strong>{opportunityGapPP.toFixed(1)} pp</strong> ahead of the largest opportunity in the cohort</>
            )}
            . Across peers, <strong>{CONCEPT_MAP[strongestCond.key].label}</strong> is the strongest workforce
            condition while <strong>{CONCEPT_MAP[weakestCond.key].label}</strong> is the most constrained — the
            upstream condition most likely to limit how readiness converts into sustained performance and value
            transfer.
          </p>
          <Link
            to={`/solution-finder?kpi=${PE_CONDITION_TO_SF_KPI[weakestCond.key]}&pe_condition=${encodeURIComponent(CONCEPT_MAP[weakestCond.key].label)}`}
            className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md transition-colors"
            data-testid="pe-navigation-signal-cta"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Strengthen {CONCEPT_MAP[weakestCond.key].label} in Solution Finder
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </CardContent>
      </Card>

      {/* 2. Multi-Org Trend Overlay Chart */}
      <Card data-testid="multi-org-trend-chart">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-500" />
              {({ pe: 'PE Score Trends', capability: 'Capability Trends', motivation: 'Motivation Trends', retention: 'Retention Trends', lei: 'LEI Trends' })[trendMetric]}
              {' '}— All Organizations
            </CardTitle>
            <div
              className="inline-flex rounded-md border border-slate-200 bg-white p-0.5"
              data-testid="pe-trend-metric-toggle"
              role="tablist"
            >
              {[
                { v: 'pe',         l: 'Total PE'   },
                { v: 'capability', l: 'Capability' },
                { v: 'motivation', l: 'Motivation' },
                { v: 'retention',  l: 'Retention'  },
                { v: 'lei',        l: 'LEI'        },
              ].map(opt => {
                const active = trendMetric === opt.v;
                return (
                  <button
                    key={opt.v}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTrendMetric(opt.v)}
                    data-testid={`pe-trend-metric-${opt.v}`}
                    className={`text-xs font-medium px-2.5 py-1 rounded transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {opt.l}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-1.5" data-testid="pe-trend-navigation-subtitle">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-indigo-500">Workforce Navigation Signals</p>
            <p className="text-xs text-slate-500">Track how workforce conditions evolve across peer organisations.</p>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={trendChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} />
              <RechartsTooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />
              <Legend />
              {org_scores.map((os, i) => (
                <Line
                  key={os.organization_id}
                  type="monotone"
                  dataKey={orgLabelById[os.organization_id] || os.organization_name}
                  stroke={orgColors[i % orgColors.length]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* 3. Component Heatmap */}
      <Card data-testid="component-heatmap">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Grid3X3 className="h-4 w-4 text-indigo-500" />
            Workforce Navigation Conditions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-2 px-3 text-slate-600 font-semibold">Organization</th>
                  {['capability', 'motivation', 'retention'].map((k) => (
                    <th key={k} className="text-center py-2 px-3 text-slate-600 font-semibold">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-slate-300"
                            data-testid={`pe-heatmap-concept-${k}`}
                          >
                            {CONCEPT_MAP[k].legacy}
                            <Info className="h-3 w-3 text-slate-400" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-xs font-semibold text-slate-100">{CONCEPT_MAP[k].label}</p>
                          <p className="text-xs mt-0.5">{CONCEPT_MAP[k].hint}</p>
                        </TooltipContent>
                      </Tooltip>
                    </th>
                  ))}
                  <th className="text-center py-2 px-3 text-slate-600 font-semibold">LEI</th>
                </tr>
              </thead>
              <tbody>
                {heatmapFiltered.map(h => (
                  <tr key={h.org_id} className="border-b border-slate-50">
                    <td className="py-2 px-3">
                      <OrgLabel orgId={h.org_id} fallback={h.org_name} />
                    </td>
                    <HeatCell value={h.capability} />
                    <HeatCell value={h.motivation} />
                    <HeatCell value={h.retention} />
                    <HeatCell value={h.lei} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-200" /> &lt;40%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-200" /> 40–59%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200" /> 60–79%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200" /> 80%+</span>
          </div>
        </CardContent>
      </Card>

      {/* Value Transfer Readiness — combined condition states for the focal org */}
      <Card data-testid="pe-value-transfer-readiness">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-500" />
            Value Transfer Readiness
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            How {vtrIsYou ? 'your organization' : <strong className="text-slate-700">{focalOrgName}</strong>}{' '}
            combines Readiness, Confidence &amp; Intent, and Sustained Performance Conditions into overall readiness
            for value transfer.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${TONE_BG[vtrVerdict.tone]}`}
              data-testid="pe-vtr-composite"
            >
              <span className="text-lg font-bold">{pct(vtrComposite)}%</span>
              <span className="text-xs font-semibold uppercase tracking-wide">{vtrVerdict.label}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {vtrConditions.map((c) => {
                const isLowest = c.key === vtrLowest.key;
                const kpiId = PE_CONDITION_TO_SF_KPI[c.key];
                const inner = (
                  <>
                    <span className={`w-2 h-2 rounded-full ${TONE_BG[c.state.tone].split(' ')[0]}`} />
                    <span className="text-xs text-slate-500">{c.concept}</span>
                    <span className="text-sm font-bold text-slate-800">{pct(c.value)}%</span>
                    <span className="text-[10px] font-semibold uppercase text-slate-400">{c.state.label}</span>
                    {isLowest && (
                      <span className="text-[10px] font-semibold uppercase text-indigo-600 inline-flex items-center gap-1">
                        · Binding constraint <ArrowRight className="h-3 w-3" />
                      </span>
                    )}
                  </>
                );
                if (isLowest && kpiId) {
                  return (
                    <Link
                      key={c.key}
                      to={`/solution-finder?kpi=${kpiId}&pe_condition=${encodeURIComponent(c.concept)}`}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400 transition-colors"
                      data-testid={`pe-vtr-condition-${c.key}`}
                      title={`Strengthen ${c.concept} in Solution Finder`}
                    >
                      {inner}
                    </Link>
                  );
                }
                return (
                  <div
                    key={c.key}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white"
                    data-testid={`pe-vtr-condition-${c.key}`}
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            Value transfers fastest when all three conditions hold together. The lowest condition above is the
            binding constraint — click it to model interventions that strengthen it in Solution Finder.
          </p>
        </CardContent>
      </Card>

      {/* 4. AI Best Practice Insights */}
      <Card className="border-2 border-violet-100" data-testid="ai-insights-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              What Top Performers Have In Common
              <span className="text-xs font-normal text-slate-400">Powered by GPT-4o</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              {insights && (
                <Button variant="ghost" size="sm" onClick={() => setShowInsights(!showInsights)} className="gap-1 text-xs">
                  {showInsights ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  {showInsights ? 'Hide' : 'Show'}
                </Button>
              )}
              <Button
                variant={insights ? "outline" : "default"}
                size="sm"
                onClick={generateInsights}
                disabled={insightsLoading}
                className="gap-1.5"
                data-testid="generate-insights-btn"
              >
                {insightsLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {insights ? 'Regenerate' : 'Generate Insights'}
              </Button>
            </div>
          </div>
        </CardHeader>
        {topPerformers.length > 0 && (
          <div className="px-6 pb-3" data-testid="pe-top-performer-deltas">
            <p className="text-xs text-slate-500 mb-2">
              Where the top {topCount === 1 ? 'performer' : `${topCount} performers`} lead the peer average:
            </p>
            <div className="flex flex-wrap gap-2">
              {conditionDeltas.map((d) => {
                const up = d.delta >= 0;
                return (
                  <div
                    key={d.key}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${up ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                    data-testid={`pe-top-performer-delta-${d.key}`}
                  >
                    {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    <span>{up ? '+' : ''}{d.delta.toFixed(1)} pp</span>
                    <span className="font-normal text-slate-500">{d.concept}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {showInsights && (
          <CardContent>
            {insightsLoading ? (
              <div className="flex items-center gap-3 py-8 justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-violet-500" />
                <span className="text-sm text-slate-500">Analyzing portfolio of {org_scores.length} organizations...</span>
              </div>
            ) : insights ? (
              <div className="prose prose-sm max-w-none">
                <MarkdownRenderer text={anonymizeNarrative(insights.insights)} />
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-400">
                  <span>Organizations: {insights.organizations_analyzed}</span>
                  <span>Period: {insights.period}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 py-4 text-center">
                Click "Generate Insights" for AI-powered analysis of top performers, gaps, and cross-org recommendations.
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {/* 5. Cohort-Level Deep Comparison */}
      <Card data-testid="cohort-rankings">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-500" />
              Cohort Rankings (All Organizations)
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCohorts(!showCohorts)}
              className="gap-1 text-xs"
              data-testid="toggle-cohort-rankings"
            >
              {showCohorts ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showCohorts ? 'Hide' : `Show All (${cohort_rankings.length})`}
            </Button>
          </div>
        </CardHeader>
        {showCohorts && (
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left py-2 px-3 text-slate-600 font-semibold">#</th>
                    <th className="text-left py-2 px-3 text-slate-600 font-semibold">Cohort</th>
                    <th className="text-left py-2 px-3 text-slate-600 font-semibold">Organization</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-semibold">PE</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-semibold">Cap</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-semibold">Mot</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-semibold">Ret</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-semibold">LEI</th>
                  </tr>
                </thead>
                <tbody>
                  {cohort_rankings.map((cr, idx) => (
                    <tr key={cr.cohort_id} className={`border-b border-slate-50 ${idx < 3 ? 'bg-emerald-50/20' : idx >= cohort_rankings.length - 3 ? 'bg-rose-50/20' : ''}`}>
                      <td className="py-2 px-3 text-slate-400 font-medium">{idx + 1}</td>
                      <td className="py-2 px-3 font-medium text-slate-800">{anonymizeCohortName(cr.cohort_name)}</td>
                      <td className="py-2 px-3 text-slate-500 text-xs">
                        <OrgLabel orgId={cr.organization_id} fallback={cr.organization_name} />
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${getPEBg(cr.pe)}`}>
                          {pct(cr.pe)}%
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center text-slate-600">{pct(cr.capability)}%</td>
                      <td className="py-2 px-3 text-center text-slate-600">{pct(cr.motivation)}%</td>
                      <td className="py-2 px-3 text-center text-slate-600">{pct(cr.retention)}%</td>
                      <td className="py-2 px-3 text-center text-slate-600">{pct(cr.lei)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>
      </>)}
    </div>
    </TooltipProvider>
    </PageShell>
  );
}
